<template>
  <div
    class="more-options"
    :class="{'more-options--mobile': isMobile}"
  >
    <button
      :class="buttonClasses"
      aria-label="More filters"
      @click="handleToggleOptions"
      @keydown.space="handleToggleOptions"
    >
      <TagIcon
        :icon="FiltersIcon"
        color="mono-black"
        class="h-5 w-5"
      />
      <span class="none sm:inline ml-2 -mr-1">
        Filters
      </span>
      <span
        v-if="selectedOptionsNumber"
        class="ml-2"
      >
        ({{ selectedOptionsNumber }})
      </span>
    </button>

    <BaseModal
      v-if="desktopOpen"
      class-name="more-options__modal"
      :show-close-button="false"
      is-open
      @close="closeModal"
    >
      <Header
        title="More filters"
        icon-position="right"
        class="more-options__modal__header"
        @close="closeModal"
      />

      <MoreFilters
        class="p-6 more-options__modal__content"
        :form="form"
        @change="change"
      />

      <ClearAndSubmit
        class="more-options__modal__footer"
        :is-loading="isLoading"
        :submit-label="submitLabel"
        @clear="clear"
        @submit="onSubmit"
      />
    </BaseModal>

    <MobilePopover
      v-if="mobileOpen"
      title="More filters"
      lock-background-scroll
      :full-height="true"
      @close="submit"
    >
      <MoreFilters
        :form="form"
        :is-mobile="true"
        @change="change"
      />

      <template #actions>
        <ClearAndSubmit
          :submit-label="submitLabel"
          :is-loading="isLoading"
          @clear="clear"
          @submit="onSubmit"
        />
      </template>
    </MobilePopover>
  </div>
</template>

<script>
import FiltersIcon from '@tagvenue/icons/24/line/general/settings-04.svg';
import { markRaw } from 'vue';
import { mapState } from 'vuex';

import TagIcon from '@/components/TagIcon.vue';
import Header from '@/components/Ui/MobilePopover/Header.vue';
import MobilePopover from '@/components/Ui/MobilePopover/MobilePopover.vue';
import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import ClearAndSubmit from '@/modules/public/search/components/SearchFilters/components/ClearAndSubmit.vue';

import SearchOptionShared from '../SearchOptionShared.js';
import MoreFilters from './MoreFilters.vue';

export default {
  name: 'MoreOptions',
  mixins: [SearchOptionShared],
  components: { ClearAndSubmit, Header, BaseModal, MoreFilters, TagIcon, MobilePopover },
  data() {
    return {
      FiltersIcon: markRaw(FiltersIcon),
    };
  },
  computed: {
    ...mapState([
      'mobileView',
    ]),
    form() {
      const {
        priceRange,
        priceMethod,
        catering,
        roomFeatures,
        allowedEvents,
        venueFeatures,
        flexibleCancellation,
        noAgeRestriction,
        superVenuesOnly,
      } = this.$store.state.filters;
      return {
        catering: this.getObjectFromFlags(catering.value),
        roomFeatures: {
          ...this.getObjectFromFlags(roomFeatures.value),
        },
        allowedEvents: this.getObjectFromFlags(allowedEvents.value),
        venueFeatures: this.getObjectFromFlags(venueFeatures.value),
        flexibleCancellation: flexibleCancellation.value,
        noAgeRestriction: noAgeRestriction.value,
        superVenuesOnly: superVenuesOnly.value,
        priceMethod: priceMethod.value,
        priceRange: [priceRange.value.from, priceRange.value.to],
      };
    },
    selectedOptionsNumber() {
      const { filters } = this.$store.state;
      let result = 0;
      result += filters.catering.value.length;
      result += filters.roomFeatures.value.length;
      result += filters.allowedEvents.value.length;
      result += filters.venueFeatures.value.length;
      result += filters.flexibleCancellation.value ? 1 : 0;
      result += filters.noAgeRestriction.value ? 1 : 0;
      result += filters.superVenuesOnly.value ? 1 : 0;
      result += filters.venueParts.value.length;
      result += filters.venueTypes.value.length;
      result += this.$store.getters.appliedAccessibilityFilters.length;
      result += filters.layout.value ? 1 : 0;
      result += filters.roomTags.value.length;

      if (this.isMobile) {
        result += !this.$store.getters.isPriceFilterDefault ? 1 : 0;
        result += filters.priceMethod.value ? 1 : 0;
      }

      if (this.$store.getters.experimentChipsFiltersVisible) {
        result += filters.customPriceRange.value ? 1 : 0;
        result += filters.customRoomFeatures.value?.length ?? 0;
        result += this.$store.getters.activeEventTypeChip ? 1 : 0;
      }
      return result;
    },
    isSelected() {
      if (this.mobileView === 'map') {
        return false;
      }
      return this.selectedOptionsNumber > 0;
    },
  },
  watch: {
    desktopOpen(value) { // Position sticky and two navbars with Popovers require zIndex hacking.
      if (value) {
        document.querySelector('.ds-header').style.zIndex = '1000';
      } else {
        document.querySelector('.ds-header').style.removeProperty('z-index');
      }
    },
    '$store.state.triggerSection': {
      handler(value) {
        if (['catering', 'venueTypes', 'areaTypes', 'accessibilityFeatures'].includes(value)) {
          this.desktopOpen = true;
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleToggleOptions() {
      this.toggleOpen();

      this.$store.dispatch('trackGA4Interaction', {
        interaction: 'click',
        section: 'list',
        element: 'advanced_filters',
        element_value: null,
      });
    },
    clear() {
      if (this.isMobile) {
        this.clearPrice();
      }
      this.$store.commit('flexibleCancellationChange', false);
      this.$store.commit('superVenuesOnlyChange', false);
      this.$store.commit('noAgeRestrictionChange', false);
      this.$store.commit('setVenueParts', []);
      this.$store.commit('clearAccessibilityFeatures', []);
      this.$store.commit('layoutChange', null);
      this.$store.commit('setAdvancedFilter', { section: 'catering', value: [] });
      this.$store.commit('setAdvancedFilter', { section: 'roomFeatures', value: [] });
      this.$store.commit('setAdvancedFilter', { section: 'allowedEvents', value: [] });
      this.$store.commit('setAdvancedFilter', { section: 'venueFeatures', value: [] });
      this.$store.commit('setAdvancedFilter', { section: 'venueTypes', value: [] });
      this.$store.commit('setAdvancedFilter', { section: 'customPriceRange', value: null });
      this.$store.commit('setAdvancedFilter', { section: 'customRoomFeatures', value: [] });
      this.$store.commit('setAdvancedFilter', { section: 'roomTags', value: [] });
      // when adding new filters, make sure to add them to the resetFilters method in search/store/mutations.js
      this.apply({ name: 'RemoveFilter', value: 'more-options' });
    },
    clearPrice() {
      this.$store.commit('priceMethodChange', null);
      this.$store.commit('resetPriceRange');
    },
    closeModal() {
      this.desktopOpen = false;
      this.mobileOpen = false;
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },
    change({ field, key, value }) {
      if (field === 'flexibleCancellation') {
        this.$store.commit('flexibleCancellationChange', value);
        this.apply({ name: 'flexibleCancellation', value });
        return;
      }
      if (field === 'noAgeRestriction') {
        this.$store.commit('noAgeRestrictionChange', value);
        this.apply({ name: 'noAgeRestriction', value });
        return;
      }
      if (field === 'superVenuesOnly') {
        this.$store.commit('superVenuesOnlyChange', value);
        this.apply({ name: 'superVenuesOnly', value });
        return;
      }

      if (field === 'venueParts') {
        this.$store.commit('setVenueParts', value);
        this.apply({ name: 'VenueParts', value });
        return;
      }

      if (field === 'venueTypes') {
        this.$store.commit('setVenueTypes', value);
        this.apply({ name: 'VenueTypes', value });
        return;
      }

      if (field === 'accessibilityFeatures') {
        this.$store.commit('setAccessibilityFeature', { key, value });
        this.apply({ name: 'AccessibilityFeatures', value: key });
        return;
      }

      if (field === 'price') {
        this.apply({ name: 'Price', value });
        return;
      }

      if (field === 'priceMethod') {
        const val = value === this.form.priceMethod ? null : value;
        this.$store.commit('priceMethodChange', val);
        this.$store.commit('resetPriceRange');
        this.apply({ name: 'priceMethod', value: val });
        return;
      }
      if (field === 'priceRange') {
        this.$store.commit('priceChange', value);
        return;
      }

      if (field === 'roomLayout') {
        this.$store.commit('layoutChange', value);
        this.apply({ name: 'Layout', value });
        return;
      }

      // Room tags and chip-only room features are flat lists rather than form flags.
      if (field === 'roomTags' || field === 'customRoomFeatures') {
        this.toggleArrayFilter(field, key, value);
        return;
      }

      if (field === 'customPriceRange') {
        // Only a price range slug is a valid value here, and every valid one is truthy - so
        // collapse anything falsy to null rather than serialising it as an active price_range.
        const priceRange = value || null;
        this.$store.commit('setAdvancedFilter', { section: field, value: priceRange });
        this.apply({ name: `AdvancedFilter-${field}`, value: priceRange });
        return;
      }

      const valuesArray = this.getFlagsFromObject({
        ...this.form[field],
        [key]: value,
      });
      this.$store.commit('setAdvancedFilter', { section: field, value: valuesArray });
      this.apply({ name: `AdvancedFilter-${field}`, value: valuesArray });
    },
    toggleArrayFilter(section, key, isSelected) {
      // String comparison: room tag ids hydrated from the URL are strings, chip values numbers.
      const remaining = this.$store.state.filters[section].value
        .filter(item => String(item) !== String(key));
      const value = isSelected ? [...remaining, key] : remaining;

      this.$store.commit('setAdvancedFilter', { section, value });
      this.apply({ name: `AdvancedFilter-${section}`, value });
    },
    getFlagsFromObject(object) {
      const result = [];
      Object.keys(object).forEach(key => {
        if (object[key]) {
          result.push(key);
        }
      });
      return result;
    },
    getObjectFromFlags(flags = []) {
      return flags.reduce((acc, key) => ({
        ...acc,
        [key]: true,
      }), {});
    },
    onSubmit() {
      if (!this.$store.getters.isNoFiltersApplied) {
        this.$store.commit('setSelectedFiltersReactive', true);
      }

      this.submit(); // from mixin

      if (this.$store.getters.isNoFiltersApplied) {
        this.$store.commit('setSelectedFiltersReactive', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use '../optionButton';
@use '@sass/design-system/base-modal';
@use '@sass/design-system/variables' as ds-variables;

.option__button {
  @include optionButton.optionButton;

  & {
    border-right: 1px solid ds-variables.$white;
  }
}

.option__button:hover,
.option__button--selected,
.option__button--active {
  border-right: 1px solid ds-variables.$grey-300;
}

.more-options {
  :deep(.more-options__modal) {
    padding: 0 !important;

    :deep(.ds-modal-content) {
      max-height: calc(100vh - #{base-modal.$modal-wrapper-padding * 2});
      display: flex;
      flex-direction: column;
    }

    .more-options__modal__header,
    .more-options__modal__footer {
      flex-shrink: 0;
    }

    .more-options__modal__content {
      overflow-y: auto;
    }
  }

  &.more-options--mobile .option__button {
    border-right: none;
  }
}

.more-options:not(.more-options--mobile) .more-options__modal__content {
  max-height: calc(100dvh - #{base-modal.$modal-wrapper-padding * 2} - 64px - 64px - 8px); // 64px - header, 64px - buttons/footer, 8px - empty modal header
}

.more--options__hr {
  border-top: 1px solid ds-variables.$grey-300;
  margin-top: 1rem;
}
</style>
